import React, { ChangeEvent, useCallback, useState } from "react";
import SearchInput from "../../../../../ui/widgets/search-input";
import { AutocompleteProps, CybwellSuggestedAddress } from "./types";

import "../styles.scss";
import Autocomplete from "../index";

export default function AddressAutocomplete({
  label,
  shrink,
  id,
  doSearch,
  onSearchInputChange,
  onSelect,
  itemToSuggestion,
  placeholder,
  selected,
  value,
  error,
  helperText,
  onBlur,
}: AutocompleteProps): React.JSX.Element {
  const [items, setItems] = useState<CybwellSuggestedAddress[]>([]);
  const [itemsMap, setItemsMap] = useState<{
    [k: string]: CybwellSuggestedAddress;
  }>({});

  /*
   * How to render the value to the user.
   */
  const suggestionToDisplayValue = useCallback(
    (selectedSuggestion: string): string => {
      return selectedSuggestion ? selectedSuggestion.split(",")[0] : "";
    },
    [],
  );

  const searchCallback = useCallback(
    async (searchTerm: string): Promise<void> => {
      const searchResults = await doSearch(searchTerm);
      const searchResultsMap = searchResults
        ? searchResults.reduce(
            (acc, obj) => ({ ...acc, ...{ [itemToSuggestion(obj)]: obj } }),
            {},
          )
        : {};
      setItems(searchResults);
      setItemsMap(searchResultsMap);
    },
    [doSearch, itemToSuggestion],
  );

  const changeInputCallback = useCallback(
    (selectedSuggestion: string): void => {
      onSelect(itemsMap[selectedSuggestion]);
    },
    [onSelect, itemsMap],
  );

  return (
    <Autocomplete
      key={id}
      id={id}
      dropdownItems={items}
      itemToSuggestion={itemToSuggestion}
      initialSelectedItem={itemToSuggestion(selected)}
      onChange={changeInputCallback}
      // @ts-expect-error Check
      stateReducer={(state, changes) => {
        // Handling an edge case where if selecting the same dropdown item twice
        // the plugin will not update the input text, so we manually handle the change ourselves as
        // the new state(changes) have the correct text value.
        if (state.selectedItem === changes.selectedItem) {
          changeInputCallback(changes.selectedItem);
          state.inputValue = changes.selectedItem;
        }
        return changes;
      }}
      // @ts-expect-error Check
      renderInput={(downshiftProps): React.JSX.Element => {
        const { ref, getInputProps } = downshiftProps;
        return (
          <SearchInput
            label={label}
            shrink={shrink}
            value={value}
            placeholder={placeholder}
            InputProps={{
              inputRef: ref,
              ...getInputProps({
                onChange: (event: ChangeEvent<HTMLInputElement>): void => {
                  searchCallback(event.target.value);
                  if (typeof onSearchInputChange === "function") {
                    onSearchInputChange(event);
                  }
                },
                onBlur,
              }),
              id,
              name: id,
              value: suggestionToDisplayValue(value),
            }}
            error={error}
            helperText={helperText}
          />
        );
      }}
    />
  );
}
